<template>
  <div class="error-logs-component flex flex-col gap-4 py-5">
    <empty-screen
      v-if="isListEmpty && !computedIsLoading"
      icon="bug"
      :title="tr('No error logs found - all systems running smoothly')"
      :flag-text="tr('System healthy')"
      :description="tr('When issues occur with your automations, they will be logged here for troubleshooting. Currently, all your automated processes are working as intended.')"
    />

    <ul v-else class="m-0 flex list-none flex-col gap-2 p-0">
      <li v-for="row in list" :key="row.id" class="glass-tile flex flex-col gap-2 rounded-xl p-4">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
          <span class="tabular-nums">{{ formatToYMD(row.created_at) }} {{ formatTime(row.created_at) }}</span>
          <ui-badge :variant="row.type === 'facebook_error' ? 'destructive' : 'secondary'">
            {{ getTypeLabel(row.type) }}
          </ui-badge>
          <a
            v-if="row.source_profile"
            :href="sourceProfileLink(row.source_profile)"
            target="_blank"
            class="text-primary hover:underline"
          >
            {{ row.source_profile.name }} ({{ row.source_profile.platform }})
          </a>
          <router-link
            v-if="!rule && row.rule"
            :to="'/automation/' + row.rule.id + '/overview'"
            class="min-w-0 truncate text-primary hover:underline"
          >
            {{ row.rule.name }}
          </router-link>
        </div>
        <!-- The log text is generated by the backend and may carry markup. -->
        <p class="m-0 text-sm text-foreground [overflow-wrap:anywhere]" v-html="row.text.replace(/([^>])\n/g, '$1<br />\n')" />
      </li>

      <template v-if="computedIsLoading || hasMore">
        <li
          v-for="i in (isListEmpty ? 3 : 1)"
          :key="'error-log-skeleton-' + i"
          ref="scrollPagerTrigger"
          class="glass-tile flex flex-col gap-2 rounded-xl p-4"
        >
          <!-- The lines of a loaded entry, with placeholders of the same size. -->
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <ui-skeleton class="h-3 w-28" />
            <ui-skeleton class="h-5.5 w-24 rounded-md" />
            <ui-skeleton class="h-3 w-32" />
          </div>
          <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-3/4" /></span>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import RuleEntity from '@/entities/Rule.js';
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import CompanyService from '@/services/Company.js';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import { formatTime, formatToYMD } from '@/utils/date';

const ITEM_PER_PAGE = 20;

export default {
  name: 'ErrorLogs',
  components: {
    EmptyScreen,
  },
  mixins: [ ScrollPagerMixin ],
  props: {
    rule: {
      type: [ RuleEntity, null ],
      required: false,
      default: () => null,
    },
    waitForRule: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      isLoading: false,
      hasMore: true,
      page: 0,
      list: [],
    };
  },
  computed: {
    isListEmpty() {
      return this.list.length == 0;
    },
    computedIsLoading() {
      return this.isLoading || (this.waitForRule && !this.rule);
    },
  },
  watch: {
    rule: {
      immediate: true,
      handler() {
        if (this.waitForRule && !this.rule) {
          return;
        }

        this.page = 0;
        this.hasMore = true;
        this.list = [];
        this.loadNewData();
      },
    },
  },
  methods: {
    formatToYMD,
    formatTime,
    loadNewData() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      this.isLoading = true;
      ++this.page;

      const params = {
        id: this.$root.currentCompany.id,
        page: this.page,
        limit: ITEM_PER_PAGE,
        ruleId: this.rule ? this.rule.id : null,
      };

      CompanyService.getErrorLogs(params)
        .then(resp => {
          resp.forEach(item => this.list.push(item));
          this.hasMore = resp.length === ITEM_PER_PAGE;

          return resp;
        })
        .catch(err => {
          this.hasMore = false;
          this.error(err);
        })
        .finally(() => {
          this.isLoading = false;
          if (this.hasMore) {
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadNewData'));
          }
        });
    },
    getTypeLabel(type) {
      switch (type) {
        case 'infinite_notice':
          return this.tr('Notice');

        case 'facebook_error':
          return this.tr('Facebook API Error');

        default:
          return type;
      }
    },
    sourceProfileLink(sourceProfile) {
      switch (sourceProfile.platform) {
        case 'facebook':
          return 'https://www.facebook.com/' + sourceProfile.external_id;

        case 'instagram':
          return 'https://www.instagram.com/' + sourceProfile.name;
      }

      return null;
    },
  },
};
</script>
